import React, { createContext, useEffect, useState } from 'react'

import useAuth from 'hooks/useAuth'
import Spinner from 'components/Spinner'
import AuthInterstitial from 'components/Factset/AuthInterstitial'
import { useUnleashContext, useFlag } from '@unleash/proxy-client-react'
import { FEATURE_FLAG_STANDALONE_PROVISIONING } from 'constants/featureFlag'
import { NEW_AUTH_KEY } from 'constants/localStorage'

const SessionStateContext = createContext()
const SessionHandlerContext = createContext()

function SessionProvider({ children }) {
  const { state, handlers } = useAuth()
  const updateContext = useUnleashContext()
  const isStandaloneProvisioning = useFlag(FEATURE_FLAG_STANDALONE_PROVISIONING)
  const [areFeatureFlagsReady, setAreFeatureFlagsReady] = useState(false)

  // Point the Unleash context at the current user, then mark flags ready. Only after this resolves
  // does useFlag reflect the user-scoped flag value rather than the anonymous default — so the
  // NEW_AUTH_KEY effect below never acts on a stale value.
  useEffect(() => {
    if (!state.currentUser?.id) {
      return
    }

    let isMounted = true
    async function applyUserContext() {
      try {
        await updateContext({ userId: state.currentUser.id })
      } catch {
        // ignore — fall back to cached/default flags; they refresh on the next poll or page load
      } finally {
        if (isMounted) {
          setAreFeatureFlagsReady(true)
        }
      }
    }
    applyUserContext()

    return () => {
      isMounted = false
    }
  }, [updateContext, state.currentUser])

  // Persists standalone-provisioning (FactSet auth) state so loginWithRedirect can read it before
  // the FF client loads. Wait for areFeatureFlagsReady so we never clear NEW_AUTH_KEY off the
  // anonymous-context `false` before the user-scoped flags resolve. Skip when signed out / session
  // expired / needs login: the user needs NEW_AUTH_KEY intact to get connection:'factset' on Sign In.
  useEffect(() => {
    if (
      !areFeatureFlagsReady ||
      state.isSignedOut ||
      state.isSessionExpired ||
      state.needsFactsetLogin
    ) {
      return
    }
    if (isStandaloneProvisioning) {
      localStorage.setItem(NEW_AUTH_KEY, 'true')
    } else {
      localStorage.removeItem(NEW_AUTH_KEY)
    }
  }, [
    areFeatureFlagsReady,
    isStandaloneProvisioning,
    state.isSignedOut,
    state.isSessionExpired,
    state.needsFactsetLogin,
  ])

  if (state.isSignedOut) {
    return <AuthInterstitial message="You successfully logged out" onSignIn={handlers.signIn} />
  }

  if (state.isSessionExpired) {
    return (
      <AuthInterstitial
        message="You've been logged out due to inactivity"
        onSignIn={handlers.signIn}
      />
    )
  }

  if (state.needsFactsetLogin) {
    return <AuthInterstitial message="Log in to your account" onSignIn={handlers.signIn} />
  }

  if (state.isLoading || state.currentUser === null) {
    return <Spinner size={75} fullPage />
  }

  return (
    <SessionStateContext.Provider value={state}>
      <SessionHandlerContext.Provider value={handlers}>
        {state.currentUser && children}
      </SessionHandlerContext.Provider>
    </SessionStateContext.Provider>
  )
}

function useSessionState() {
  const context = React.useContext(SessionStateContext)
  if (context === undefined) {
    throw new Error('useSessionState must be used within an SessionProvider')
  }
  return context
}

function useSessionHandlers() {
  const context = React.useContext(SessionHandlerContext)
  if (context === undefined) {
    throw new Error('useSessionHandlers must be used within an SessionProvider')
  }
  return context
}

export { useSessionState, useSessionHandlers, SessionProvider, SessionHandlerContext }
